import { Button, Stack } from '@mui/material';
import {
  GetStaffQueryFilters,
  GetStaffQuerySort,
  StaffArrangeBy,
  StaffSearchResult,
  useGetChartQuery,
  useGetStaffQuery,
} from '@schooly/api';
import { useGetLabel } from '@schooly/components/charts';
import {
  ArrangedByCollapsableSection,
  ArrangedByCollapsableSectionSkeleton,
  getCombinedRowsFromSearchResults,
} from '@schooly/components/filters';
import { MainGridNoResultsStub } from '@schooly/components/stubs';
import { PROPERTIES_TEXT_IDS, SchoolUserRole } from '@schooly/constants';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { EyeIcon, GridBody, MainPageGrid, SkeletonRows } from '@schooly/style';
import { FC, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { StaffHeader, StaffRow } from './StaffGrid';

type StaffArrangedByListProps = {
  schoolId: string;
  arrangeBy: StaffArrangeBy;
  filters: GetStaffQueryFilters;
  showZeroCounts?: boolean;
  sort?: GetStaffQuerySort;
  query?: string;
  onChangeSort: (v: GetStaffQuerySort) => void;
  onSetTotalCount: (v?: number) => void;
};
export const StaffArrangedByList: FC<StaffArrangedByListProps> = ({
  schoolId,
  arrangeBy,
  filters,
  showZeroCounts = true,
  sort,
  query,
  onChangeSort,
  onSetTotalCount,
}) => {
  const { $t } = useIntl();

  const { schoolProperties, isLoading: isLoadingSchoolProperties } = useSchoolProperties({
    userType: SchoolUserRole.Staff,
    schoolId,
    showReEnrollmentProperties: true,
  });
  const { data, isLoading } = useGetChartQuery(
    {
      schoolId,
      filters,
      arrange_by: arrangeBy,
      entityType: 'staff',
      query,
    },
    {
      refetchOnMount: 'always',
      enabled: !!schoolId && !!arrangeBy,
    },
  );

  const getDataName = useGetLabel({
    arrangeBy,
    schoolProperties,
  });

  const totalCount = useMemo(() => {
    return data?.series?.reduce<number>(
      (acc, s) => acc + (s.data?.reduce((acc, d) => acc + d, 0) ?? 0),
      0,
    );
  }, [data]);

  useEffect(() => {
    onSetTotalCount(totalCount);
  }, [onSetTotalCount, totalCount]);

  const sections = useMemo(() => {
    if (!data) return [];

    return data.arrange_by.data
      .map(({ name, dataValue }, i) => ({
        title: getDataName({ dataKey: data.arrange_by.dataKey, dataValue, name }),
        count: data.series[0].data[i],
        isNoneSection: false,
        arrangeByKey: arrangeBy,
        arrangeByValue: dataValue ?? undefined,
      }))
      .filter(({ count }) => (showZeroCounts ? true : count > 0));
  }, [data, getDataName, arrangeBy, showZeroCounts]);

  if (isLoading || isLoadingSchoolProperties) return <ArrangedByCollapsableSectionSkeleton />;

  if (!sections.length) return <MainGridNoResultsStub textId="staff-NoResults-title" />;

  return (
    <>
      {sections.map((section) => (
        <ArrangedByCollapsableSection
          key={section.arrangeByKey + section.arrangeByValue}
          isExpanded={sections.length === 1}
          count={section.count}
          title={
            section.isNoneSection
              ? `${$t({ id: 'without' })}  ${$t({ id: PROPERTIES_TEXT_IDS[section.arrangeByKey] })}`
              : section.title
          }
        >
          <ArrangedByStaffGrid
            count={section.count}
            sort={sort}
            filters={filters}
            arrangeByKey={section.arrangeByKey}
            arrangeByValue={section.isNoneSection ? undefined : section.arrangeByValue}
            schoolId={schoolId}
            query={query}
            onChangeSort={onChangeSort}
          />
        </ArrangedByCollapsableSection>
      ))}
    </>
  );
};

type ArrangedByStaffGridProps = {
  count: number;
  filters: GetStaffQueryFilters;
  arrangeByKey?: StaffArrangeBy;
  arrangeByValue?: string | number;
  schoolId: string;
  sort?: GetStaffQuerySort;
  query?: string;
  onChangeSort: (v: GetStaffQuerySort) => void;
};

const PAGE_SIZE = 30;
const HEADER_OFFSET = 50;

export const ArrangedByStaffGrid: FC<ArrangedByStaffGridProps> = ({
  count,
  sort,
  filters: initialFilters,
  schoolId,
  onChangeSort,
  arrangeByKey,
  arrangeByValue,
  query,
}) => {
  const filters = useMemo(
    () =>
      arrangeByKey
        ? {
            ...initialFilters,
            [arrangeByKey]: [arrangeByValue === undefined ? 'none' : arrangeByValue],
          }
        : initialFilters,
    [initialFilters, arrangeByKey, arrangeByValue],
  );

  const { data, isLoading, params, setParams, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetStaffQuery(
      {
        schoolId,
        pageSize: PAGE_SIZE,
        sort,
        filters,
        query,
      },
      { refetchOnMount: 'always' },
    );

  useEffect(() => {
    setParams((params) => ({ ...params, filters, sort, query }));
  }, [sort, filters, query, setParams]);

  const entries = useMemo(
    () =>
      getCombinedRowsFromSearchResults(
        data?.pages.reduce((prev, curr) => [...prev, ...curr.results], [] as StaffSearchResult[]) ??
          [],
      ),
    [data?.pages],
  );

  return (
    <MainPageGrid
      bottomElement={
        hasNextPage && !isFetchingNextPage ? (
          <Stack mt={1} alignItems="center">
            <Button startIcon={<EyeIcon />} variant="text" onClick={() => fetchNextPage()}>
              <FormattedMessage id="action-ShowMoreButton" />
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <StaffHeader
        sort={params.sort}
        onChangeSort={onChangeSort}
        sx={{
          position: 'sticky',
          bgcolor: 'white',
          zIndex: 3,
          top: HEADER_OFFSET,
        }}
      />
      <GridBody>
        {entries?.map((entry) => (
          <StaffRow schoolId={schoolId} combinedStaffRow={entry} key={entry.id} />
        ))}
        {(isLoading || isFetchingNextPage) && (
          <SkeletonRows
            columnsCount={6}
            amount={Math.min(PAGE_SIZE, count - (entries?.length || 0))}
          />
        )}
      </GridBody>
    </MainPageGrid>
  );
};
